import Papa from "papaparse";

export const parseThinkorSwimCSV = (csvContent) => {
  console.log("Starting CSV parse...");
  const results = Papa.parse(csvContent, {
    header: false,
    skipEmptyLines: true,
  });

  const lines = results.data;
  let trades = [];
  let accountBalance = 0;

  const headerRow = lines.findIndex(
    (row) =>
      row.includes("DATE") &&
      row.includes("TIME") &&
      row.includes("DESCRIPTION") &&
      row.includes("AMOUNT")
  );

  if (headerRow === -1) {
    console.error("Could not find header row");
    return { trades: [], accountBalance: 0, dailyPnL: 0 };
  }

  console.log("Found header row at index:", headerRow);

  const headers = lines[headerRow];
  const dateIndex = headers.indexOf("DATE");
  const timeIndex = headers.indexOf("TIME");
  const descIndex = headers.indexOf("DESCRIPTION");
  const amountIndex = headers.indexOf("AMOUNT");
  const balanceIndex = headers.indexOf("BALANCE");

  // Get latest balance first
  for (let i = headerRow + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line || !line[balanceIndex]) continue;
    const balance = parseFloat(line[balanceIndex].replace(/[,$]/g, ""));
    if (!isNaN(balance)) {
      accountBalance = balance;
      break;
    }
  }

  const tradeParts = [];

  for (let i = headerRow + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line[descIndex] || line[descIndex] === "TOTAL") continue;

    const desc = line[descIndex];
    if (desc.includes("BOT") || desc.includes("SOLD")) {
      try {
        const parsedTrade = parseTradeRow(line, {
          dateIndex,
          timeIndex,
          descIndex,
          amountIndex,
        });

        if (parsedTrade) {
          console.log("Parsed trade:", parsedTrade);
          tradeParts.push({
            ...parsedTrade,
            accountBalance: accountBalance,
          });
        }
      } catch (err) {
        console.error("Error parsing trade row:", err);
      }
    }
  }

  console.log("Total parsed trades:", tradeParts.length);
  trades = processTradesInOrder(tradeParts);
  console.log("Processed trades:", trades);

  return {
    trades,
    accountBalance,
    dailyPnL: calculateDailyPnL(trades),
  };
};

function parseTradeRow(row, indices) {
  const { dateIndex, timeIndex, descIndex, amountIndex } = indices;
  const description = row[descIndex];

  const parts = description.split(" ");
  const action = description.includes("BOT") ? "BOT" : "SOLD";

  let quantity = 0;
  let symbol = "";
  let strike = null;
  let expiry = null;

  // Parse quantity and symbol
  for (let i = 0; i < parts.length; i++) {
    const num = parseInt(parts[i]);
    if (!isNaN(num)) {
      quantity = Math.abs(num);
      symbol = parts[i + 1];
      break;
    }
  }

  // Parse price
  const priceMatch = description.match(/@\s*(\d*\.?\d+)/);
  const price = priceMatch ? parseFloat(priceMatch[1]) : 0;

  // Parse option details
  const isOption = description.includes("CALL") || description.includes("PUT");
  if (isOption) {
    const dateMatch = description.match(/\d{1,2}\/\d{1,2}\/\d{2}/);
    if (dateMatch) {
      expiry = dateMatch[0];
    }

    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === "CALL" || parts[i] === "PUT") {
        strike = parseFloat(parts[i - 1]);
        break;
      }
    }
  }

  const optionType = description.includes("CALL")
    ? "CALL"
    : description.includes("PUT")
    ? "PUT"
    : null;

  return {
    date: `${row[dateIndex]} ${row[timeIndex]}`,
    action,
    symbol,
    quantity,
    price,
    isOption,
    optionType,
    strike,
    expiry,
  };
}

function getDisplaySymbol(position) {
  if (position.isOption && position.strike) {
    const optionType = position.optionType === "CALL" ? "C" : "P";
    return `${position.symbol} ${position.strike}${optionType}`;
  }
  return position.symbol;
}

function processTradesInOrder(tradeParts) {
  console.log("Processing trades in chronological order...");

  // Sort trades chronologically
  tradeParts.sort((a, b) => new Date(a.date) - new Date(b.date));

  const openPositions = new Map();
  const completedTrades = [];

  for (const trade of tradeParts) {
    const key = getPositionKey(trade);

    if (trade.action === "BOT") {
      if (!openPositions.has(key)) {
        openPositions.set(key, {
          entryDate: trade.date,
          symbol: trade.symbol,
          totalQuantity: 0,
          averageEntry: 0,
          exits: [],
          isOption: trade.isOption,
          optionType: trade.optionType,
          strike: trade.strike,
          expiry: trade.expiry,
          accountBalance: trade.accountBalance,
        });
      }

      const position = openPositions.get(key);
      position.averageEntry =
        (position.averageEntry * position.totalQuantity +
          trade.price * trade.quantity) /
        (position.totalQuantity + trade.quantity);
      position.totalQuantity += trade.quantity;

      console.log(
        `Added ${trade.quantity} contracts to position ${key}. ` +
          `Total: ${position.totalQuantity}, Avg Entry: ${position.averageEntry}`
      );
    } else {
      // Handle sell
      const position = openPositions.get(key);
      if (position) {
        position.exits.push({
          quantity: trade.quantity,
          price: trade.price,
        });

        const totalSold = position.exits.reduce(
          (sum, exit) => sum + exit.quantity,
          0
        );
        console.log(
          `Sold ${trade.quantity} contracts from position ${key}. ` +
            `Total sold: ${totalSold}/${position.totalQuantity}`
        );

        if (totalSold === position.totalQuantity) {
          const avgExit =
            position.exits.reduce(
              (sum, exit) => sum + exit.price * exit.quantity,
              0
            ) / totalSold;

          const pnl =
            (avgExit - position.averageEntry) *
            position.totalQuantity *
            (position.isOption ? 100 : 1);

          console.log(
            `Closing position ${key}. ` +
              `Entry: ${position.averageEntry}, Exit: ${avgExit}, ` +
              `P&L: ${pnl}`
          );

          completedTrades.push({
            date: position.entryDate,
            symbol: getDisplaySymbol(position),
            direction: position.optionType === "PUT" ? "SHORT" : "LONG",
            entryPrice: position.averageEntry,
            exitPrice: avgExit,
            quantity: position.totalQuantity,
            pnl,
            isOption: position.isOption,
            optionType: position.optionType,
            status: pnl >= 0 ? "WIN" : "LOSS",
            broker: "tos",
            accountBalance: position.accountBalance,
          });

          openPositions.delete(key);
        }
      }
    }
  }

  return completedTrades;
}

function getPositionKey(trade) {
  if (trade.isOption) {
    return `${trade.symbol}_${trade.strike}_${trade.expiry}_${trade.optionType}`;
  }
  return trade.symbol;
}

function calculateDailyPnL(trades) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return trades.reduce((sum, trade) => {
    const tradeDate = new Date(trade.date);
    tradeDate.setHours(0, 0, 0, 0);

    if (tradeDate.getTime() === today.getTime()) {
      return sum + trade.pnl;
    }
    return sum;
  }, 0);
}

export const validateTOSFormat = (csvContent) => {
  const results = Papa.parse(csvContent, { header: false });
  const lines = results.data;

  const hasHeaders = lines.some(
    (line) =>
      line.includes("DATE") &&
      line.includes("TIME") &&
      line.includes("DESCRIPTION") &&
      line.includes("AMOUNT")
  );

  return {
    isValid: hasHeaders,
    message: hasHeaders
      ? "Valid ThinkOrSwim format"
      : "Invalid file format. Please upload a ThinkOrSwim account statement.",
  };
};
